import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { List, Typography, Empty } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash } from '@fortawesome/pro-light-svg-icons';

import './DisabledTab.sass';

import { getItemAlteredByScenario } from 'utils/rules';
import ActionButton from 'components/action/ActionButton';
import UIContext from 'components/Utils/UIContext';

interface DisabledItem {
	id: string;
	name: string;
	type: string;
	__typename: string;
	isOpportunity?: boolean;
	fishboneBranchId?: string;
}

interface DisabledTabProps {
	scenario: any;
	stakeholders?: any[];
	risks?: any[];
	stakes?: any[];
	indicators?: any[];
	fishboneBranchs?: any[];
	fishboneItems?: any[];
	onEnableItem?: (item: DisabledItem) => void;
	onDeleteItem?: (item: DisabledItem) => void;
	select?: (item: any) => void;
	splitRisksAndOpportunities?: boolean;
	/** Si true, masque les items dont la branche parente est désactivée (spécifique Fishbone) */
	hideItemsOfDisabledBranches?: boolean;
}

const getDisabledItems = (
	items: any[],
	scenario: any,
	typeName: string
): DisabledItem[] => {
	if (!items) return [];

	return items
		.map((item) => {
			const altered = getItemAlteredByScenario(item, scenario);
			return {
				...item,
				...altered,
				__typename: typeName
			};
		})
		.filter((item) => item.enabled === false);
};

export const DisabledTab = (props: DisabledTabProps) => {
	const { t } = useTranslation();
	// Lecteurs (lecture seule) : ni suppression ni réactivation — le serveur les
	// refuse de toute façon, on ne les propose pas.
	const { canEdit } = useContext(UIContext);
	const onDeleteItem = canEdit ? props.onDeleteItem : undefined;
	const onEnableItem = canEdit ? props.onEnableItem : undefined;

	const disabledStakeholders = getDisabledItems(
		props.stakeholders || [],
		props.scenario,
		'Stakeholder'
	);
	const disabledRisks = getDisabledItems(
		props.risks || [],
		props.scenario,
		'Risk'
	);
	const disabledStakes = getDisabledItems(
		props.stakes || [],
		props.scenario,
		'Stake'
	);
	const disabledConstraints = getDisabledItems(
		props.indicators || [],
		props.scenario,
		'Indicator'
	);
	const disabledFishboneBranchs = getDisabledItems(
		props.fishboneBranchs || [],
		props.scenario,
		'FishboneBranch'
	);
	
	// IDs des branches désactivées pour filtrer leurs items si demandé
	const disabledBranchIds = new Set(disabledFishboneBranchs.map((b) => b.id));
	
	// Map des branches pour récupérer le nom de la branche parente
	const branchesMap = new Map((props.fishboneBranchs || []).map((b) => [b.id, b.name]));
	
	const disabledFishboneItems = getDisabledItems(
		props.fishboneItems || [],
		props.scenario,
		'FishboneItem'
	).filter((item) => {
		// Si hideItemsOfDisabledBranches est activé, on masque les items dont la branche est désactivée
		if (props.hideItemsOfDisabledBranches && item.fishboneBranchId && disabledBranchIds.has(item.fishboneBranchId)) {
			return false;
		}
		return true;
	}).map((item) => ({
		...item,
		branchName: branchesMap.get(item.fishboneBranchId) || ''
	}));
	
	// Grouper les items fishbone par branche (pour l'affichage avec headers)
	const fishboneItemsByBranch = disabledFishboneItems.reduce((acc, item) => {
		const branchId = item.fishboneBranchId || 'unknown';
		if (!acc[branchId]) {
			acc[branchId] = {
				branchName: item.branchName,
				items: []
			};
		}
		acc[branchId].items.push(item);
		return acc;
	}, {} as Record<string, { branchName: string; items: any[] }>);

	// Séparer risques et opportunités si demandé
	const disabledRisksOnly = disabledRisks.filter((item) => !item.isOpportunity).map((item) => ({
		...item,
		type: t('models.risk.name')
	}));
	const disabledOpportunitiesOnly = disabledRisks.filter((item) => item.isOpportunity).map((item) => ({
		...item,
		type: t('models.risk.name_positive')
	}));

	// Pour le mode fishbone avec groupement par branche
	const fishboneBranchItems = disabledFishboneBranchs.map((item) => ({
		...item,
		type: t('models.fishbone_branch.name'),
		isBranch: true
	}));

	// Pour les autres modes (sans groupement)
	const allDisabledItems = [
		...disabledStakeholders.map((item) => ({
			...item,
			type: t('models.item.name')
		})),
		...disabledRisks.map((item) => ({
			...item,
			type: item.isOpportunity
				? t('models.risk.name_positive')
				: t('models.risk.name')
		})),
		...disabledStakes.map((item) => ({
			...item,
			type: t('models.stake.name')
		})),
		...disabledConstraints.map((item) => ({
			...item,
			type: t('models.indicator.name')
		})),
		...fishboneBranchItems,
		...(props.hideItemsOfDisabledBranches ? [] : disabledFishboneItems.map((item) => ({
			...item,
			type: t('models.fishbone_item.name'),
			isBranch: false
		})))
	];

	const handleSelect = (item: any) => {
		if (props.select) {
			props.select(item);
		}
	};

	const handleEnable = (item: DisabledItem, e: React.MouseEvent) => {
		e.stopPropagation();
		if (onEnableItem) {
			onEnableItem(item);
		}
	};

	const handleDelete = (item: DisabledItem, e: React.MouseEvent) => {
		e.stopPropagation();
		if (onDeleteItem) {
			onDeleteItem(item);
		}
	};

	const renderList = (items: any[], title?: string, isSecondList?: boolean) => (
		<>
			{title && items.length > 0 && (
				<Typography.Text strong className={`${isSecondList ? 'mt-2' : 'mt-1'} mb-05 d-block`}>
					{title}
				</Typography.Text>
			)}
			{items.length > 0 && (
				<List
					size="small"
					dataSource={items}
					renderItem={(item: any) => (
					<List.Item className="deactivated-item">
						<div className="item-content">
							{onDeleteItem && (
								<ActionButton
									className="xsmall delete-action"
									tooltip={t('actions.delete')}
									onClick={(e) => handleDelete(item, e)}
								>
									<FontAwesomeIcon icon={faTrash} />
								</ActionButton>
							)}
							<span className="item-name">{item.name}</span>
							{onEnableItem && (
								<ActionButton
									className="xsmall"
									tooltip={t('actions.enable')}
									onClick={(e) => handleEnable(item, e)}
								>
									<FontAwesomeIcon icon={faEye} />
								</ActionButton>
							)}
						</div>
					</List.Item>
					)}
				/>
			)}
		</>
	);

	// Mode séparé pour risques et opportunités
	if (props.splitRisksAndOpportunities) {
		const hasRisks = disabledRisksOnly.length > 0;
		const hasOpportunities = disabledOpportunitiesOnly.length > 0;
		const isEmpty = !hasRisks && !hasOpportunities;

		return (
			<div className="DeactivatedItemsTab">
				<Typography.Title level={5} className="mb-1">
					{t('pages.disabled.title')}
				</Typography.Title>

				{isEmpty ? (
					<Empty
						description={t('pages.disabled.empty')}
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				) : (
					<>
						{renderList(disabledRisksOnly, t('models.risk.name_plural'), false)}
						<br />
						{renderList(disabledOpportunitiesOnly, t('models.risk.name_plural_positive'), true)}
					</>
				)}
			</div>
		);
	}

	// Rendu d'un item de liste
	const renderItem = (item: any) => (
		<List.Item className={`deactivated-item ${item.isBranch ? 'is-branch' : ''}`}>
			<div className="item-content">
				{onDeleteItem && (
					<ActionButton
						className="xsmall delete-action"
						tooltip={t('actions.delete')}
						onClick={(e) => handleDelete(item, e)}
					>
						<FontAwesomeIcon icon={faTrash} />
					</ActionButton>
				)}
				<span className="item-name">{item.name}</span>
				{onEnableItem && (
					<ActionButton
						className="xsmall"
						tooltip={t('actions.enable')}
						onClick={(e) => handleEnable(item, e)}
					>
						<FontAwesomeIcon icon={faEye} />
					</ActionButton>
				)}
			</div>
		</List.Item>
	);

	// Calcul du total pour le mode fishbone avec groupement
	const totalFishboneDisabled = fishboneBranchItems.length + disabledFishboneItems.length;
	const hasFishboneItemsGrouped = props.hideItemsOfDisabledBranches && Object.keys(fishboneItemsByBranch).length > 0;

	return (
		<div className="DeactivatedItemsTab">
			<Typography.Title level={5} className="mb-1">
				{t('pages.disabled.title')}
			</Typography.Title>

			{allDisabledItems.length === 0 && !hasFishboneItemsGrouped ? (
				<Empty
					description={t('pages.disabled.empty')}
					image={Empty.PRESENTED_IMAGE_SIMPLE}
				/>
			) : (
				<>
					{/* Items non-fishbone et branches fishbone */}
					{allDisabledItems.length > 0 && (
						<List
							size="small"
							dataSource={allDisabledItems}
							renderItem={renderItem}
						/>
					)}
					
					{/* Items fishbone groupés par branche (mode fishbone uniquement) */}
					{hasFishboneItemsGrouped && Object.entries(fishboneItemsByBranch).map(([branchId, group]) => (
						<div key={branchId} className="fishbone-branch-group">
							<Typography.Text className="branch-header">
								{group.branchName}
							</Typography.Text>
							<List
								size="small"
								dataSource={group.items.map((item) => ({
									...item,
									type: t('models.fishbone_item.name'),
									isBranch: false
								}))}
								renderItem={renderItem}
							/>
						</div>
					))}
				</>
			)}
		</div>
	);
};

export default DisabledTab;

